import React from 'react';
import { useHistory } from 'react-router-dom';
import { UserRoles } from '@bb/users';
import styled from 'styled-components';

import { Button, Space, Icon, Dropdown, Menu, MenuItem } from 'components';

import Searchbar from './Searchbar';

import ManageUsersContext from '../ManageUsersContext';
import { StyledUsersCount } from '../StyledComponents';
import { TeamGroupModalContext } from '../TeamGroupModal/TeamGroupModalContext';
import TeamGroupRemovalConfirmation from '../Tables/TeamGroupRemovalConfirmation';
import { TEAM_ADMIN_DELETE_MSG } from '../Tables/constants';

export interface TeamGroupMemberHeaderProps {
  md: boolean;
  type: 'group' | 'team';
}

const getTitle = (type: 'group' | 'team', hasTeamAdmins: boolean) => {
  if (type === 'team' && hasTeamAdmins) {
    return TEAM_ADMIN_DELETE_MSG;
  }
  return `Are you sure you’d like to delete this ${type}?`;
};

const TeamGroupMemberHeader: React.FC<TeamGroupMemberHeaderProps> = ({
  md,
  type,
}) => {
  const history = useHistory();

  const {
    setSearchText,
    searchText,
    selectedGroupMemberCount,
    selectedTeamMemberCount,
    selectedTeamInfo,
    groupId,
    teamId,
  } = React.useContext(ManageUsersContext);

  const { openModal } = React.useContext(TeamGroupModalContext);

  const [popconfirmVisibility, setPopconfirmVisibility] = React.useState(false);

  const hasTeamAdmins = React.useMemo(() => {
    if (type === 'group' || !selectedTeamInfo) return false;
    return (
      selectedTeamInfo.users.findIndex(
        user => user.role === UserRoles['Team Admin'],
      ) !== -1
    );
  }, [type, selectedTeamInfo]);

  const { typeName, count, onEdit, onDelete } =
    type === 'group'
      ? {
          typeName: 'Group',
          count: selectedGroupMemberCount,
          onEdit: () => openModal('group', 'edit', groupId),
          onDelete: () =>
            history.push(
              `/users/list?tabKey=${type}s&deleteGroupId=${groupId}`,
            ),
        }
      : {
          typeName: 'Team',
          count: selectedTeamMemberCount,
          onEdit: () => openModal('team', 'edit', teamId),
          onDelete: () =>
            history.push(`/users/list?tabKey=${type}s&deleteTeamId=${teamId}`),
        };

  const textChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <Container wrap size={md ? 'small' : 'middle'}>
      {md && (
        <StyledUsersCount>
          {count} User{count !== 1 && 's'}
        </StyledUsersCount>
      )}

      <Searchbar
        allowClear
        placeholder="Search Users"
        value={searchText}
        onChange={textChangeHandler}
        suffix={<Icon type="search" color="gray5" />}
      />

      {!md && (
        <StyledUsersCount $hideMargin>
          {count} User{count !== 1 && 's'}
        </StyledUsersCount>
      )}

      <Dropdown
        trigger={['click']}
        dropdownRender={() => (
          <div onClick={e => e.stopPropagation()}>
            <TeamGroupRemovalConfirmation
              canDelete={type === 'group' || !hasTeamAdmins}
              title={getTitle(type, hasTeamAdmins)}
              visible={popconfirmVisibility}
              onCancel={() => setPopconfirmVisibility(false)}
              handleDelete={onDelete}
              popconfirmProps={{ placement: 'bottomRight' }}
            >
              <Menu>
                <MenuItem key={'edit'} onClick={onEdit}>
                  Edit {typeName}
                </MenuItem>
                <MenuItem
                  key={'delete'}
                  onClickCapture={e => {
                    setPopconfirmVisibility(true);
                    e.stopPropagation();
                  }}
                >
                  Delete {typeName}
                </MenuItem>
              </Menu>
            </TeamGroupRemovalConfirmation>
          </div>
        )}
      >
        <Button
          type="text"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <Icon type="ellipsis" width={25} />
        </Button>
      </Dropdown>
    </Container>
  );
};

const Container = styled(Space)`
  justify-content: flex-end;
  width: 100%;
`;

export default TeamGroupMemberHeader;
